import React from 'react';

export interface HeadingProps {
  children: React.ReactNode;
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  className?: string;
  variant?: 'default' | 'section' | 'subsection' | 'title' | 'subtitle';
  id?: string;
  style?: React.CSSProperties;
}

/**
 * Heading component for consistent typography
 * Renders the appropriate heading level (h1-h6) with consistent styling
 */
const Heading: React.FC<HeadingProps> = ({
  children,
  level = 2,
  className = '',
  variant = 'default',
  id,
  ...rest
}) => {
  // Base styles that apply to all heading levels
  const baseStyles = 'font-medium text-text-primary dark:text-text-primary';
  
  // Variant styles
  const variantStyles = {
    default: '',
    section: 'mb-4',
    subsection: 'mb-2',
    title: 'text-lg sm:text-xl md:text-2xl mb-4 font-bold',
    subtitle: 'text-base sm:text-lg mb-2 text-text-secondary',
  };
  
  // Size styles based on heading level
  const sizeStyles = {
    1: 'text-2xl sm:text-3xl md:text-4xl',
    2: 'text-xl sm:text-2xl md:text-3xl',
    3: 'text-lg sm:text-xl md:text-2xl',
    4: 'text-base sm:text-lg',
    5: 'text-sm sm:text-base',
    6: 'text-xs sm:text-sm',
  };
  
  // Combine all styles
  const combinedClassName = `${baseStyles} ${sizeStyles[level]} ${variantStyles[variant]} ${className}`;
  
  // Render the appropriate heading tag based on level
  const Tag = `h${level}` as keyof JSX.IntrinsicElements;
  
  return (
    <Tag className={combinedClassName} id={id} {...rest}>
      {children}
    </Tag>
  );
};

export default Heading;